/* eslint-disable react/react-in-jsx-scope */
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { FileBadge, Loader, Trash } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const TableSkeleton = ({ columns = 4, rows = 3 }) => {
  return (
    <Table className="w-full border-collapse">
      <TableHeader>
        <TableRow>
          {[...Array(columns)].map((_, colIndex) => (
            <TableHead key={colIndex}>
              <Skeleton height={20} width="80%" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...Array(rows)].map((_, rowIndex) => (
          <TableRow key={rowIndex}>
            {[...Array(columns)].map((_, colIndex) => (
              <TableCell key={colIndex}>
                <Skeleton height={18} width="90%" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
type Financeur = {
  id: string;
  name: string;
};
type Projet = {
  id: string;
  name: string;
};

type File = {
  name: string;
  link: string;
};

type Certificat = {
  id: string;
  financeur: Financeur;
  projet: Projet;
  montant: string;
  date: string;
  file: File;
};

const Certificats = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "certificats",
  });
  const { postData } = usePostData();
  const [isLoading, setIsloading] = useState(true);
  const certificatsData = useFetchDataV2(
    `${import.meta.env.VITE_API_ENDPOINT}/certificats`,
    "certificats",
    1000 * 60 * 60
  );
  const [data, setData] = useState<Certificat[]>([]);

  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const navigate = useNavigate();

  const onOpenChange = (isOpen: boolean) => {
    setOpenModalDelete(isOpen);
  };

  useEffect(() => {
    if (certificatsData?.data !== undefined) {
      setData(
        certificatsData?.data?.length
          ? certificatsData?.data?.map((certificat: any) => ({
              id: certificat.id,

              projet: certificat.project as Projet,
              montant: certificat.montant,
              date: certificat.dateVersement,
              file: {
                name: certificat.cerfa,
                link: "",
              } as File,
            }))
          : ([] as Certificat[])
      );
      setIsloading(false);
    }
  }, [certificatsData?.data]);

  /*useEffect(() => {
    setIsloading(false);
    setData(certificatsData);
  }, []);*/

  const { firstname } = useParams();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );

  const projetOptions = React.useMemo(() => {
    return Array.from(new Set(data.map((c) => c.projet.name))).sort();
  }, [data]);

  const handleFinanceurChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setColumnFilters(
      (old) =>
        value
          ? [
              ...old.filter((f) => f.id !== "financeur"),
              { id: "financeur", value },
            ]
          : old.filter((f) => f.id !== "financeur") // supprime le filtre si valeur vide
    );
  };

  const handleProjetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setColumnFilters((old) =>
      value
        ? [...old.filter((f) => f.id !== "projet"), { id: "projet", value }]
        : old.filter((f) => f.id !== "projet")
    );
  };

  const financeurFilterValue =
    columnFilters.find((f) => f.id === "financeur")?.value ?? "";
  const projetFilterValue =
    columnFilters.find((f) => f.id === "projet")?.value ?? "";

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const columns: ColumnDef<Certificat>[] = [
    {
      accessorKey: "projet",
      header: t("Projet"),
      // accessorFn: (row) => row.projet.name,
      size: 80, // taille "souhaitée"
      minSize: 80, // taille minimale autorisée
      maxSize: 200,
      cell: ({ row }) => {
        const projet = row.getValue("projet") as Projet;
        return (
          <div>
            <a
              href={`/${firstname}/project/${projet.id}`}
              className="text-blue-600 hover:underline"
            >
              {projet.name}
            </a>
          </div>
        );
      },
      filterFn: (row, id, filterValue) => {
        return row.original.projet.name
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      },
    },
    {
      accessorKey: "montant",
      header: t("Montant"),
      cell: ({ row }) => {
        return (
          <div>
            {Number(row.getValue("montant")).toLocaleString("fr-FR", {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })}
          </div>
        );
      },
    },
    {
      accessorKey: "date",
      header: t("Date du versement"),
      cell: ({ row }) => {
        const dateValue = row.getValue("date");
        if (typeof dateValue === "string" && dateValue.includes("#")) {
          const [start, end] = dateValue.split("#");
          return (
            <div>
              Du {start} au {end}
            </div>
          );
        } else {
          return <div>{dateValue}</div>;
        }
      },
    },
    {
      accessorKey: "file",
      header: t("Fichier"),
      cell: ({ row }) => {
        const file = row.getValue("file") as File;
        // const downloadUrl = `${import.meta.env.VITE_API_ENDPOINT}/download-certificate/${file.name}`;
        const handleDownload = async () => {
          try {
            const token =
              localStorage.getItem("jwtToken") ??
              sessionStorage.getItem("jwtToken");
            const filename = encodeURIComponent(file.name);
            const response = await fetch(
              `${
                import.meta.env.VITE_API_ENDPOINT
              }/download-certificate?filename=${filename}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`, // Remplace par ton vrai token ou méthode d'accès
                },
              }
            );

            if (!response.ok) {
              throw new Error("Erreur lors du téléchargement");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url); // Nettoyage
          } catch (error) {
            console.error("Erreur :", error);
            alert(t("Échec du téléchargement du fichier"));
          }
        };
        return (
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 text-blue-600 hover:underline"
          >
            <FileBadge size={18} className="text-red-500" />
            {file.name}
          </button>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const handleDelete = async () => {
    setLoadingDelete(true);
    // get ids selected
    const ids = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    // delete selected
    postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/delete-certificate",
      data: {
        ids,
      },
    }).then((res) => {
      if (res.success !== undefined && res.success) {
        setData((prev) => prev.filter((item) => !ids.includes(item.id)));
        onOpenChange(false);
        toast({
          title: t("Les certificats sélectionnés ont bien été supprimé."),
        });
      }

      setLoadingDelete(false);
    });
  };

  return (
    <div>
      <ReactBreadcrumb
        items={[
          {
            href: `/`,
            name: t("Tableau de bord"),
          },
          {
            href: ``,
            name: t("Mes Certificats"),
          },
        ]}
        isTruncated={false}
      />

      <div className="flex flex-wrap gap-5 items-center justify-between mt-5">
        <h1
          className="mb-2 mt-4 text-[24px] font-medium inline-block"
          id="program-title"
        >
          {t("Mes Certificats")}
        </h1>
      </div>

      <div className="flex gap-4 items-center max-w-md mt-4">
        <div className="w-40 relative">
          <select
            className="appearance-none block w-full rounded-md border border-gray-300 bg-white py-1.5 px-2
                      pr-7 text-sm font-medium text-gray-700 shadow-sm focus:border-indigo-500
                      focus:outline-none focus:ring-1 focus:ring-indigo-500"
            value={projetFilterValue}
            onChange={handleProjetChange}
          >
            <option value="">{t("Tous les projets")}</option>
            {projetOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-700">
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 8l4 4 4-4"
              />
            </svg>
          </div>
        </div>
      </div>

      <div className="w-full mt-5">
        <div className="rounded-md border">
          {isLoading ? (
            <>
              <TableSkeleton />
            </>
          ) : (
            <>
              <Table columns={columns}>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        {t("Pas de résultats.")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </div>
        {isLoading ? (
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              <Skeleton width={180} height={16} />
            </div>
            <div className="space-x-2 flex">
              <Skeleton width={80} height={32} borderRadius={6} />
              <Skeleton width={80} height={32} borderRadius={6} />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {t("Précédent")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {t("Suivant")}
              </Button>
            </div>
          </div>
        )}
      </div>
      <Dialog open={openModalDelete} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-h-[80vh] sm:max-w-[50vw] overflow-y-auto">
          <DialogHeader className="">
            <DialogTitle asChild>
              <h2 className="mb-2 mt-5 flex gap-4 text-center text-xl font-bold">
                {t("Suppression de certificat")}
              </h2>
            </DialogTitle>
            <DialogDescription asChild className="text-[#595959]">
              <p className="mt-3">
                {t(
                  "Voulez-vous vraiment supprimer les certificats sélectionnés ?"
                )}
              </p>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap mt-5 h-full overflow-y-auto">
            <div className="w-full flex justify-end mt-5">
              <DialogFooter className="pb-5">
                <Button
                  onClick={() => handleDelete()}
                  type="button"
                  disabled={loadingDelete}
                >
                  {loadingDelete ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      {t("... Suppression en cours")}
                    </>
                  ) : (
                    <>
                      <Trash className="w-4 h-4 mr-2" />
                      {t("Supprimer")}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Certificats;
